import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Search, FileText, CalendarIcon, ArrowRightCircle, FileDown, Pencil, Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const statusColors = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  aprobada: 'bg-green-100 text-green-800',
  cancelada: 'bg-red-100 text-red-800',
  convertida: 'bg-purple-100 text-purple-800',
};

const initialFormData = {
  cliente_id: '', cliente_nombre: 'Publico General', cliente_telefono: '',
  fecha: format(new Date(), 'yyyy-MM-dd'), hora_inicio: '10:00', hora_fin: '16:00',
  direccion: '', detalles: [], incluye_transporte: false, costo_transporte: 0,
  tipo_descuento: null, valor_descuento: 0, monto_descuento: 0,
  subtotal: 0, total: 0, anticipo_minimo: 200, anticipo: 0, saldo_pendiente: 0,
  estatus: 'pendiente', notas: '',
};

export default function CotizacionesAdminPage() {
  const { getAuthHeader } = useAuth();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [inflables, setInflables] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCotizacion, setEditingCotizacion] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState(initialFormData);
  const [selectedInflable, setSelectedInflable] = useState('');

  const fetchData = async () => {
    try {
      const [cotRes, infRes, cliRes] = await Promise.all([
        axios.get(`${API_URL}/cotizaciones`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/inflables?activo=true`),
        axios.get(`${API_URL}/clientes`, { headers: getAuthHeader() }),
      ]);
      setCotizaciones(cotRes.data);
      setInflables(infRes.data);
      setClientes(cliRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const recalculateTotals = (data) => {
    const subtotalInflables = data.detalles.reduce((sum, det) => sum + det.subtotal_item, 0);
    const subtotal = subtotalInflables + (data.incluye_transporte ? data.costo_transporte : 0);
    let monto_descuento = 0;
    if (data.tipo_descuento === 'porcentaje' && data.valor_descuento > 0) monto_descuento = subtotal * (data.valor_descuento / 100);
    else if (data.tipo_descuento === 'monto' && data.valor_descuento > 0) monto_descuento = data.valor_descuento;
    const total = Math.max(0, subtotal - monto_descuento);
    const anticipo_minimo = data.detalles.reduce((max, det) => {
      const inf = inflables.find(i => i.id === det.inflable_id);
      return Math.max(max, inf?.anticipo_requerido || 200);
    }, 200);
    const anticipo = Math.max(data.anticipo, 0);
    return { ...data, subtotal, monto_descuento, total, anticipo_minimo, saldo_pendiente: total - anticipo };
  };

  const handleAddInflable = () => {
    if (!selectedInflable) return;
    const inflable = inflables.find(i => i.id === selectedInflable);
    if (!inflable) return;
    if (formData.detalles.some(d => d.inflable_id === selectedInflable)) { toast.error('Ya esta agregado'); return; }
    const newDetalle = { inflable_id: inflable.id, inflable_nombre: inflable.nombre, precio_unitario: inflable.precio_base, horas_extra: 0, precio_hora_extra: inflable.precio_hora_extra, subtotal_item: inflable.precio_base };
    setFormData(recalculateTotals({ ...formData, detalles: [...formData.detalles, newDetalle] }));
    setSelectedInflable('');
  };

  const handleRemoveInflable = (inflableId) => {
    setFormData(recalculateTotals({ ...formData, detalles: formData.detalles.filter(d => d.inflable_id !== inflableId) }));
  };

  const handleUpdateDetalle = (inflableId, field, value) => {
    const newDetalles = formData.detalles.map(det => {
      if (det.inflable_id === inflableId) {
        const updated = { ...det, [field]: value };
        if (field === 'horas_extra') updated.subtotal_item = updated.precio_unitario + (value * updated.precio_hora_extra);
        return updated;
      }
      return det;
    });
    setFormData(recalculateTotals({ ...formData, detalles: newDetalles }));
  };

  const handleFieldChange = (field, value) => setFormData(recalculateTotals({ ...formData, [field]: value }));

  const handleOpenDialog = (cotizacion = null) => {
    if (cotizacion) {
      setEditingCotizacion(cotizacion);
      setFormData({ ...cotizacion, detalles: cotizacion.detalles || [] });
      setSelectedDate(new Date(cotizacion.fecha + 'T12:00:00'));
    } else {
      setEditingCotizacion(null);
      setFormData(initialFormData);
      setSelectedDate(new Date());
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.detalles.length === 0) { toast.error('Agrega al menos un inflable'); return; }
    try {
      if (editingCotizacion) {
        await axios.put(`${API_URL}/cotizaciones/${editingCotizacion.id}`, formData, { headers: getAuthHeader() });
        toast.success('Cotizacion actualizada');
      } else {
        await axios.post(`${API_URL}/cotizaciones`, formData, { headers: getAuthHeader() });
        toast.success('Cotizacion creada');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al guardar');
    }
  };

  const handleConvertToRenta = async (id) => {
    try {
      await axios.post(`${API_URL}/cotizaciones/${id}/convertir`, {}, { headers: getAuthHeader() });
      toast.success('Convertida a renta');
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al convertir');
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/cotizaciones/${id}/pdf`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cotizacion_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const handleDeleteCotizacion = async (id) => {
    if (!window.confirm('Eliminar esta cotizacion?')) return;
    try {
      await axios.delete(`${API_URL}/cotizaciones/${id}`, { headers: getAuthHeader() });
      toast.success('Eliminada');
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    }
  };

  const handleUpdateStatus = async (id, estatus) => {
    try {
      await axios.patch(`${API_URL}/cotizaciones/${id}/estatus?estatus=${estatus}`, {}, { headers: getAuthHeader() });
      toast.success('Estatus actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const filteredCotizaciones = cotizaciones.filter(c =>
    c.folio?.toLowerCase().includes(searchTerm.toLowerCase()) || c.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const canEdit = (cot) => cot.estatus === 'pendiente';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="cotizaciones-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Cotizaciones</h1>
          <p className="text-xs sm:text-sm text-allegra-gray">Crea y gestiona cotizaciones</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full gap-1.5 h-9 text-xs sm:text-sm sm:h-10" data-testid="add-cotizacion-btn">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nueva Cotizacion</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-allegra-gray" />
            <Input placeholder="Buscar por folio o cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-xl text-sm" data-testid="search-cotizaciones" />
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
          ) : filteredCotizaciones.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-allegra-gray/50 mx-auto mb-3" />
              <p className="text-allegra-gray text-sm">No hay cotizaciones</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {filteredCotizaciones.map((cot) => (
                  <div key={cot.id} className="p-3 bg-muted/50 rounded-xl" data-testid={`cotizacion-card-${cot.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-allegra-gray">{cot.folio}</span>
                          <Badge className={`text-[10px] h-5 ${statusColors[cot.estatus]}`}>{cot.estatus}</Badge>
                        </div>
                        <p className="font-medium text-sm text-allegra-dark mt-0.5">{cot.cliente_nombre || 'Publico General'}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs text-allegra-gray mb-2">
                      <span>{format(new Date(cot.fecha + 'T12:00:00'), 'd MMM yyyy', { locale: es })}</span>
                      <span>{cot.detalles?.length || 1} inflable(s)</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-bold text-allegra-dark">Total: ${cot.total?.toLocaleString()}</span>
                      <span className="text-xs text-green-600">Anticipo: ${cot.anticipo?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleDownloadPDF(cot.id)} data-testid={`pdf-cotizacion-${cot.id}`}>
                        <FileDown className="w-3 h-3" /> PDF
                      </Button>
                      {canEdit(cot) && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleOpenDialog(cot)} data-testid={`edit-cotizacion-${cot.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {cot.estatus === 'pendiente' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs text-green-600" onClick={() => handleUpdateStatus(cot.id, 'aprobada')}>Aprobar</Button>
                      )}
                      {(cot.estatus === 'pendiente' || cot.estatus === 'aprobada') && (
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleConvertToRenta(cot.id)} data-testid={`convert-cotizacion-${cot.id}`}>
                          <ArrowRightCircle className="w-3 h-3" /> Renta
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleDeleteCotizacion(cot.id)} data-testid={`delete-cotizacion-${cot.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Folio</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Cliente</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Fecha</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Inflables</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Total</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Anticipo</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Estatus</th>
                      <th className="text-right py-3 px-3 text-sm font-medium text-allegra-gray">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCotizaciones.map((cot) => (
                      <tr key={cot.id} className="border-b last:border-0" data-testid={`cotizacion-row-${cot.id}`}>
                        <td className="py-3 px-3 font-mono text-sm">{cot.folio}</td>
                        <td className="py-3 px-3 text-sm">{cot.cliente_nombre || 'Publico General'}</td>
                        <td className="py-3 px-3 text-sm">{format(new Date(cot.fecha + 'T12:00:00'), 'd MMM yyyy', { locale: es })}</td>
                        <td className="py-3 px-3 text-sm text-allegra-gray">{cot.detalles?.length || 1} inflable(s)</td>
                        <td className="py-3 px-3 font-bold text-sm">${cot.total?.toLocaleString()}</td>
                        <td className="py-3 px-3 text-sm text-green-600">${cot.anticipo?.toLocaleString() || 0}</td>
                        <td className="py-3 px-3"><Badge className={statusColors[cot.estatus]}>{cot.estatus}</Badge></td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex justify-end gap-1.5 flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(cot.id)} data-testid={`pdf-cotizacion-${cot.id}`}><FileDown className="w-4 h-4" /></Button>
                            {canEdit(cot) && <Button variant="outline" size="sm" onClick={() => handleOpenDialog(cot)} data-testid={`edit-cotizacion-${cot.id}`}><Pencil className="w-4 h-4" /></Button>}
                            {cot.estatus === 'pendiente' && <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleUpdateStatus(cot.id, 'aprobada')}>Aprobar</Button>}
                            {(cot.estatus === 'pendiente' || cot.estatus === 'aprobada') && <Button variant="outline" size="sm" className="gap-1" onClick={() => handleConvertToRenta(cot.id)} data-testid={`convert-cotizacion-${cot.id}`}><ArrowRightCircle className="w-4 h-4" />Renta</Button>}
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteCotizacion(cot.id)} data-testid={`delete-cotizacion-${cot.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingCotizacion ? 'Editar Cotizacion' : 'Nueva Cotizacion'}</DialogTitle>
            <DialogDescription>{editingCotizacion ? 'Modifica los datos de la cotizacion' : 'Completa los datos para cotizar'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={formData.cliente_id || 'publico'} onValueChange={(v) => {
                  if (v === 'publico') setFormData({ ...formData, cliente_id: '', cliente_nombre: 'Publico General', cliente_telefono: '' });
                  else { const c = clientes.find(c => c.id === v); setFormData({ ...formData, cliente_id: v, cliente_nombre: c?.nombre || '', cliente_telefono: c?.telefono || '' }); }
                }}>
                  <SelectTrigger data-testid="cotizacion-cliente"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publico">Publico General</SelectItem>
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input value={formData.cliente_telefono} onChange={(e) => setFormData({ ...formData, cliente_telefono: e.target.value })} placeholder="Telefono de contacto" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal text-xs sm:text-sm">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                      {format(selectedDate, "d MMM", { locale: es })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={selectedDate} onSelect={(date) => { if (date) { setSelectedDate(date); setFormData({ ...formData, fecha: format(date, 'yyyy-MM-dd') }); } }} disabled={(date) => date < new Date()} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Inicio</Label>
                <Input type="time" value={formData.hora_inicio} onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input type="time" value={formData.hora_fin} onChange={(e) => setFormData({ ...formData, hora_fin: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Direccion del Evento *</Label>
              <Input value={formData.direccion} onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} placeholder="Calle, numero, colonia" required data-testid="cotizacion-direccion" />
            </div>

            <div className="space-y-2">
              <Label>Agregar Inflables</Label>
              <div className="flex gap-2">
                <Select value={selectedInflable} onValueChange={setSelectedInflable}>
                  <SelectTrigger className="flex-1" data-testid="select-inflable"><SelectValue placeholder="Seleccionar inflable" /></SelectTrigger>
                  <SelectContent>
                    {inflables.filter(i => !formData.detalles.some(d => d.inflable_id === i.id)).map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.nombre} - ${i.precio_base}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={handleAddInflable} disabled={!selectedInflable} data-testid="add-inflable-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {formData.detalles.length > 0 && (
              <div className="space-y-2 border rounded-xl p-3">
                {formData.detalles.map((det) => (
                  <div key={det.inflable_id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{det.inflable_nombre}</p>
                      <p className="text-xs text-allegra-gray">${det.precio_unitario}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-allegra-gray">+Hrs:</span>
                        <Input type="number" min={0} value={det.horas_extra} onChange={(e) => handleUpdateDetalle(det.inflable_id, 'horas_extra', Number(e.target.value))} className="w-16 h-8 text-sm" />
                      </div>
                      <span className="font-bold text-sm whitespace-nowrap">${det.subtotal_item}</span>
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleRemoveInflable(det.inflable_id)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Costo Transporte</Label>
                <Input type="number" min={0} value={formData.costo_transporte} onChange={(e) => handleFieldChange('costo_transporte', Number(e.target.value))} onFocus={() => setFormData({ ...formData, incluye_transporte: true })} />
              </div>
              <div className="space-y-2">
                <Label>Descuento</Label>
                <div className="flex gap-1.5">
                  <Select value={formData.tipo_descuento || 'none'} onValueChange={(v) => handleFieldChange('tipo_descuento', v === 'none' ? null : v)}>
                    <SelectTrigger className="w-20"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin</SelectItem>
                      <SelectItem value="monto">$</SelectItem>
                      <SelectItem value="porcentaje">%</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" min={0} value={formData.valor_descuento} onChange={(e) => handleFieldChange('valor_descuento', Number(e.target.value))} disabled={!formData.tipo_descuento} className="flex-1" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Anticipo (minimo sugerido: ${formData.anticipo_minimo})</Label>
              <Input type="number" min={0} max={formData.total} value={formData.anticipo} onChange={(e) => handleFieldChange('anticipo', Number(e.target.value))} data-testid="cotizacion-anticipo" />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={formData.notas} onChange={(e) => setFormData({ ...formData, notas: e.target.value })} rows={2} />
            </div>

            <div className="bg-muted rounded-xl p-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-allegra-gray">Subtotal:</span><span>${formData.detalles.reduce((s, d) => s + d.subtotal_item, 0).toLocaleString()}</span></div>
              {formData.costo_transporte > 0 && <div className="flex justify-between"><span className="text-allegra-gray">Transporte:</span><span>${formData.costo_transporte}</span></div>}
              {formData.monto_descuento > 0 && <div className="flex justify-between text-green-600"><span>Descuento:</span><span>-${formData.monto_descuento.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-base border-t pt-1.5"><span>Total:</span><span className="text-primary">${formData.total.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-allegra-gray">Anticipo:</span><span className="text-green-600">${formData.anticipo.toLocaleString()}</span></div>
              <div className="flex justify-between font-bold"><span>Saldo Pendiente:</span><span className="text-amber-600">${formData.saldo_pendiente.toLocaleString()}</span></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formData.detalles.length === 0} data-testid="save-cotizacion-btn">{editingCotizacion ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
